/** Supernova's sounds (04 §5, 03): capsules and power-ups, the shield, the Nova bomb, the chain. Pure. */

import type { CapsuleKind } from '../../sim/types';
import { midiToHz } from '../notes';
import { intBelow, vary } from '../rand';
import { CAPSULE_KINDS } from '../scan';
import { adsr, type VoiceSpec } from '../voice';
import { hit, run, type Sfx, tone } from './kit';

/** A capsule appears: a soft ping, A6 with a breath of its octave. */
export const capsule: Sfx = (r) => {
  const hz = vary(r, midiToHz(93), 0.01);
  return [hit('triangle', hz, 0.25, 0.16), hit('triangle', hz * 2, 0.08, 0.05)];
};

/** Each capsule's own figure over the shared sweep: semitones above C5, and seconds a note. */
const FIGURE: Readonly<Record<CapsuleKind, { readonly notes: readonly number[]; readonly step: number }>> = {
  // A stutter: every note twice.
  rapid: { notes: [12, 12, 19, 19, 24, 24], step: 0.03 },
  // Fanned out: a major seventh chord.
  spread: { notes: [12, 16, 19, 23], step: 0.05 },
  // Straight through: octaves and a fifth, nothing between.
  pierce: { notes: [12, 24, 31], step: 0.055 },
  // Suspended: the fourth instead of the third.
  shield: { notes: [12, 17, 19, 24], step: 0.06 },
  // Time dragging: a slow minor seventh.
  chrono: { notes: [12, 15, 19, 22], step: 0.09 },
  // From the floor: the bomb being armed.
  nova: { notes: [0, 7, 12, 19, 24], step: 0.045 },
  // The bonus fighter's major arpeggio (a 1UP capsule sends no `extraLife`).
  life: { notes: [12, 16, 19, 24, 28, 31], step: 0.05 },
};

/** A capsule taken: a bright rising sweep, and the figure of its kind (`n`, as in `CAPSULE_KINDS`). */
export const powerUp: Sfx = (r, n) => {
  const figure = FIGURE[CAPSULE_KINDS[n] ?? 'rapid'];
  const gate = figure.notes.length * figure.step + 0.12;
  return [
    {
      ...tone('pulse', vary(r, 420, 0.03), 0.2, adsr(0.002, 0.1, 0.6, 0.06), 0.12),
      width: 0.5,
      to: 3300,
      glide: 0.2,
    },
    { ...run('pulse', figure.notes.map((s) => 72 + s), figure.step, gate, 0.2), width: 0.25 },
  ];
};

/** A power-up runs out: a falling blip. */
export const powerDown: Sfx = () => [
  { ...hit('pulse', 988, 0.14, 0.14), width: 0.5, to: 330, glide: 0.14 },
];

/** The shield bursts: glass - a crack of bright noise and three high pings scattering. */
export const shieldBurst: Sfx = (r) => [
  { ...hit('noise', 15000, 0.09, 0.4), filter: { type: 'highpass', freq: 4500, q: 0 } },
  ...[0, 0.03, 0.07].map((at, i): VoiceSpec => ({
    ...hit('triangle', midiToHz(96 + intBelow(r, 12)), 0.2 + i * 0.07, 0.18),
    at,
  })),
];

/** The Nova bomb: a deep boom, and a sweep and a rush of noise rising out of it. */
export const nova: Sfx = (r) => [
  { ...hit('triangle', vary(r, 120, 0.05), 0.9, 0.6), to: 28, glide: 0.8 },
  {
    ...hit('noise', 2600, 1.1, 0.6),
    to: 200,
    glide: 0.9,
    filter: { type: 'lowpass', freq: 3000, to: 150, sweep: 0.9, q: 3 },
  },
  { ...tone('pulse', 180, 0.6, adsr(0.05, 0.2, 0.8, 0.2), 0.14), width: 0.25, to: 3600, glide: 0.6 },
  {
    ...tone('noise', 2000, 0.6, adsr(0.2, 0.2, 0.7, 0.25), 0.16),
    to: 14000,
    glide: 0.6,
    filter: { type: 'highpass', freq: 1500, to: 6000, sweep: 0.6, q: 1 },
  },
];

/** The chain's notes for x2 to x5: E6, G6, A6, B6 - E minor pentatonic, at home over the Empress's theme. */
const CHAIN = [88, 91, 93, 95] as const;

/** The note for a chain multiplier (2-5; anything else is held to that range). */
export function chainMidi(level: number): number {
  const i = Number.isFinite(level) ? Math.max(0, Math.min(CHAIN.length - 1, Math.round(level) - 2)) : 0;
  return CHAIN[i] ?? CHAIN[0];
}

/** The multiplier rises (`n` is the new one): its note, flicked up from the octave below. */
export const chain: Sfx = (_r, n) => {
  const m = chainMidi(n);
  return [{ ...run('pulse', [m - 12, m], 0.035, 0.16, 0.2), width: 0.25 }];
};
